import React from 'react';

class ResizeObserverErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.errorListeners = [];
  }

  componentDidMount() {
    // Add global error listeners to catch async errors that React boundaries miss
    const handleGlobalError = (event) => {
      const errorMessage = event.error?.message || event.message || '';
      if (this.isResizeObserverError(errorMessage)) {
        event.preventDefault();
        event.stopPropagation();
        console.warn('ResizeObserver error suppressed by boundary:', errorMessage);
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      const errorMessage = event.reason?.message || event.reason?.toString() || '';
      if (this.isResizeObserverError(errorMessage)) {
        event.preventDefault();
        console.warn('ResizeObserver promise rejection suppressed by boundary:', errorMessage);
        return false;
      }
    };

    // Add listeners with capture phase to catch errors early
    window.addEventListener('error', handleGlobalError, { capture: true });
    window.addEventListener('unhandledrejection', handleUnhandledRejection, { capture: true });
    
    // Store cleanup functions
    this.errorListeners.push(
      () => window.removeEventListener('error', handleGlobalError, { capture: true }),
      () => window.removeEventListener('unhandledrejection', handleUnhandledRejection, { capture: true })
    );
  }

  componentWillUnmount() {
    // Clean up error listeners
    this.errorListeners.forEach(cleanup => cleanup());
    this.errorListeners = [];
  }

  isResizeObserverError(errorMessage) {
    return errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
           errorMessage.includes('ResizeObserver loop limit exceeded');
  }

  static getDerivedStateFromError(error) {
    // Check if this is a ResizeObserver error
    if (error && error.message && 
        (error.message.includes('ResizeObserver loop completed with undelivered notifications') ||
         error.message.includes('ResizeObserver loop limit exceeded'))) {
      // Don't update state, just suppress the error
      return null;
    }
    // For other errors, let them bubble up
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a ResizeObserver error
    if (error && error.message && this.isResizeObserverError(error.message)) {
      // Suppress ResizeObserver errors
      console.warn('ResizeObserver error suppressed by boundary:', error.message);
      return;
    }
    
    // For other errors, log them normally
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI for non-ResizeObserver errors
      return <div>Something went wrong with the drag-and-drop tree.</div>;
    }

    return this.props.children;
  }
}

export default ResizeObserverErrorBoundary;
